<template>
  <div
    v-click-outside="close"
    class="retailer-search-type-fi"
  >
    <div
      class="retailer-search-type-fi__button"
      :class="{
        'retailer-search-type-fi__button': true,
        'retailer-search-type-fi__button--open': openSelect,
      }"
      @click="toggleSelect"
    >
      <div class="retailer-search-type-fi__button__label">
        <icon-fi :icon="activeRetailersCount ? 'checkbox-checked' : 'filter'" />

        {{ facet.name }}

        <span
          v-if="activeRetailersCount"
          class="active-count"
          >({{ activeRetailersCount }})</span
        >
      </div>

      <icon-fi
        class="retailer-search-type-fi__button__icon"
        icon="chevron-down"
      />

      <div class="retailer-search-type-fi__button__label--mobile">
        <icon-fi :icon="activeRetailersCount ? 'checkbox-checked' : 'filter'" />
      </div>
    </div>

    <transition
      mode="in-out"
      appear
      leave-active-class="retailer-search-type-fi__options--leaving"
      @after-leave="close"
    >
      <div
        v-if="openSelect"
        class="retailer-search-type-fi__options"
      >
        <div class="retailer-search-type-fi__options__header">
          {{ facet.name }}

          <icon-fi
            class="retailer-search-type-fi__options__header__icon"
            icon="close"
            @click="close"
          />
        </div>

        <div class="retailer-search-type-fi__options__body">
          <retailer-search-type-options-fi
            :elements="retailerTypeOptions || undefined"
            :emit-only-on-save="!isSM"
            :partners="activePartners"
            :retailers="activeRetailers"
            @select-changed="selectChanged"
          />
        </div>

        <div class="retailer-search-type-fi__options__bottom">
          <button-fi
            class="button-fi__primary button-fi--large button-fi__xs-streched retailer-search-type-fi__options__save"
            @click="save"
          >
            {{ $dict.get('dealer-Locator.Facets.Labels.Use-Facet') }}
          </button-fi>
          <div
            class="retailer-search-type-fi__options__clear"
            @click="clear"
          >
            {{ $dict.get('dealer-Locator.Facets.Labels.Clear-Facet') }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, IconFi } from 'atoms';
import { computed, ref } from 'vue';

import { useBodyScroll } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';

import RetailerSearchTypeOptionsFi from './retailer-search-type-options-fi/RetailerSearchTypeOptionsFi.vue';

defineOptions({ name: 'RetailerSearchTypeFi' });

interface Props {
  facet: Record<string, any>;
  retailersCount?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  retailersCount: null,
});

const emit = defineEmits<{
  'retailer-type-changed': [payload: { partners: any[]; retailers: any[] }];
}>();

const { isSM } = useBreakpoints();
const { bodyDisableScroll, bodyEnableScroll } = useBodyScroll();

const openSelect = ref(false);
const active = ref(false);
const retailerTypeOptions = ref<any[] | null>(null);
const changedOptions = ref<any[] | null>(null);
const activePartners = ref<any[]>([]);
const activeRetailers = ref<any[]>([]);

// created logic
retailerTypeOptions.value = JSON.parse(JSON.stringify(props.facet.elements));

const activeRetailersCount = computed(() => {
  if (active.value) {
    return props.retailersCount;
  }
  return null;
});

const open = () => {
  openSelect.value = true;
  if (!isSM.value) {
    bodyDisableScroll();
  }
};

const close = () => {
  openSelect.value = false;
  if (!isSM.value) {
    bodyEnableScroll();
  }
};

const toggleSelect = () => {
  if (openSelect.value) {
    close();
  } else {
    open();
  }
};

const selectChanged = (val: any) => {
  const {
    newElements,
    activePartners: newActivePartners,
    activeRetailers: newActiveRetailers,
  } = val;
  changedOptions.value = newElements;
  activePartners.value = newActivePartners;
  activeRetailers.value = newActiveRetailers;

  if (isSM.value) {
    active.value = Boolean(newActivePartners.length || newActiveRetailers.length);
    retailerTypeOptions.value = newElements;
    emit('retailer-type-changed', {
      partners: newActivePartners,
      retailers: newActiveRetailers,
    });
  }
};

const save = () => {
  retailerTypeOptions.value = changedOptions.value;
  active.value = Boolean(activePartners.value.length || activeRetailers.value.length);
  emit('retailer-type-changed', {
    partners: activePartners.value,
    retailers: activeRetailers.value,
  });
  close();
};

const clear = () => {
  active.value = false;
  retailerTypeOptions.value = JSON.parse(JSON.stringify(props.facet.elements));
  activePartners.value = [];
  activeRetailers.value = [];
  emit('retailer-type-changed', { partners: [], retailers: [] });
  close();
};
</script>

<style scoped src="./retailer-search-type-fi" />
